/**
 * QZ Tray Certificate Configuration
 * Based on: https://qz.io/docs/signing
 *
 * Setup untuk menghilangkan popup konfirmasi saat print
 * Menggunakan API backend untuk certificate dan signing
 *
 * KONFIGURASI:
 *
 * 1. USE_SIGNING = true (default)
 *    - Setiap print akan request signature ke backend
 *    - Tidak ada popup konfirmasi
 *    - Cocok untuk production
 *
 * 2. USE_SIGNING = false
 *    - Tidak ada request ke backend untuk signature
 *    - Akan muncul popup konfirmasi setiap print
 *    - User harus klik "Allow" untuk melanjutkan
 *    - Cocok untuk development/testing jika backend tidak tersedia
 *
 * 3. FALLBACK_ON_SIGNING_ERROR = true (default)
 *    - Jika API signature error, akan fallback ke mode tanpa signature
 *    - Print tetap bisa dilakukan, tapi akan muncul popup konfirmasi
 *    - Mengurangi dependency ke backend saat backend down
 */

import qz from 'qz-tray'
import { getQZTrayCertificate, signQZTrayMessage } from '@/utils/apis/pos'

// Configuration
const USE_SIGNING = true // Set true untuk enable signing (no popup!)
const FALLBACK_ON_SIGNING_ERROR = true // Jika true, akan fallback ke mode tanpa signature jika API error

// Cache untuk certificate (hanya di-load sekali)
let cachedCertificate = null
let certificateLoadingPromise = null
let certificateSetupDone = false

/**
 * Setup certificate signing untuk QZ Tray
 * Mengikuti tutorial resmi: https://qz.io/docs/signing
 */
export const setupQZCertificate = () => {
  try {
    // Jika sudah di-setup sebelumnya, jangan setup ulang
    if (certificateSetupDone) {
      console.log('🔐 QZ Tray certificate sudah di-setup sebelumnya, skip setup')
      return true
    }

    console.log('🔐 Setting up QZ Tray certificate...')

    if (!USE_SIGNING) {
      console.log('⚠️ Signing disabled - popup akan muncul saat connect')
      console.log('💡 Set USE_SIGNING = true untuk enable signing')
      return false // Return false untuk indicate certificate tidak di-setup
    }

    // Set signature algorithm (SHA512 untuk QZ Tray 2.1+)
    qz.security.setSignatureAlgorithm('SHA512')

    /**
     * setCertificatePromise
     * Dipanggil saat initial websocket connection
     * Load public certificate dari API backend (dengan cache)
     *
     * Note: Jika certificate tidak ditemukan, kita tetap resolve dengan empty string
     * agar QZ Tray tidak hang. User akan diminta "Allow" pada popup.
     */
    qz.security.setCertificatePromise((resolve) => {
      // Jika certificate sudah di-cache, gunakan cache
      if (cachedCertificate !== null) {
        console.log('📜 Using cached certificate')
        resolve(cachedCertificate)
        return
      }

      // Jika sedang loading, tunggu promise yang sudah ada
      if (certificateLoadingPromise) {
        console.log('📜 Certificate sedang di-load, menunggu...')
        certificateLoadingPromise
          .then((certificateData) => {
            resolve(certificateData)
          })
          .catch(() => {
            resolve('')
          })
        return
      }

      // Load certificate dari API (hanya sekali)
      console.log('📜 Loading certificate from API...')
      certificateLoadingPromise = getQZTrayCertificate()
        .then((response) => {
          const certificateData = response.data
          if (certificateData && certificateData.trim()) {
            console.log('✅ Certificate loaded successfully')
            // Cache certificate untuk penggunaan selanjutnya
            cachedCertificate = certificateData
            certificateLoadingPromise = null
            resolve(certificateData)
            return certificateData
          }
          // Certificate kosong atau tidak ditemukan
          console.warn('⚠️ Certificate kosong atau tidak ditemukan')
          console.warn('   QZ Tray akan tetap bisa digunakan')
          console.warn('   Akan muncul popup konfirmasi - klik "Allow" untuk melanjutkan')
          // Cache empty string juga untuk menghindari API call berulang
          cachedCertificate = ''
          certificateLoadingPromise = null
          resolve('')
          return ''
        })
        .catch((error) => {
          console.warn('⚠️ Error loading certificate:', error.message)
          console.warn('   QZ Tray akan tetap bisa digunakan, tapi akan muncul popup konfirmasi')
          console.warn(
            '   Untuk menghilangkan popup, pastikan endpoint /qz-sign/certificate tersedia',
          )
          console.warn('   Atau klik "Allow" pada popup yang muncul')
          // Cache empty string untuk menghindari API call berulang
          cachedCertificate = ''
          certificateLoadingPromise = null
          resolve('')
        })
    })

    /**
     * setSignaturePromise
     * Dipanggil pada setiap print call
     * Request signature dari API backend
     *
     * @param {string} toSign - Message yang akan di-sign
     */
    qz.security.setSignaturePromise((toSign) => {
      return (resolve) => {
        // Jika signing disabled, langsung skip
        if (!USE_SIGNING) {
          console.log('📝 Signing disabled, skipping signature request')
          resolve(null)
          return
        }

        console.log('📝 Requesting signature from API...')

        // Request signature dari API backend
        signQZTrayMessage(toSign)
          .then((response) => {
            const signature = response.data
            if (signature && signature.trim()) {
              console.log('✅ Request signed successfully')
              resolve(signature)
            } else {
              throw new Error('Empty signature received')
            }
          })
          .catch((error) => {
            if (FALLBACK_ON_SIGNING_ERROR) {
              console.warn('⚠️ Error signing request:', error.message)
              console.warn('   Fallback: Print tanpa signature (akan muncul popup konfirmasi)')
              console.warn('   User perlu klik "Allow" untuk melanjutkan print')
              // Fallback: resolve dengan null untuk skip signing
              // QZ Tray akan tetap bisa print, tapi akan muncul popup konfirmasi
              resolve(null)
            } else {
              // Jika fallback disabled, reject untuk mencegah print
              console.error('❌ Signing failed and fallback disabled:', error.message)
              console.error('   Print dibatalkan karena signature diperlukan')
              // Reject untuk mencegah print tanpa signature
              throw error
            }
          })
      }
    })

    // Tandai bahwa setup sudah selesai
    certificateSetupDone = true

    console.log('✅ QZ Tray certificate setup completed')
    console.log('📚 Documentation: https://qz.io/docs/signing')
    return true
  } catch (error) {
    console.error('❌ QZ Tray certificate setup failed:', error)
    return false
  }
}

/**
 * Setup dengan certificate untuk production
 *
 * CARA MENDAPATKAN CERTIFICATE:
 * 1. Request gratis dari: https://qz.io/request-certificate/
 * 2. Generate sendiri (self-signed) - masih ada warning
 * 3. Beli dari Certificate Authority - fully trusted
 *
 * CONTOH IMPLEMENTASI PRODUCTION:
 */
export const setupQZCertificateProduction = (certificate, signingServerUrl) => {
  try {
    qz.security.setSignatureAlgorithm('SHA512')

    // Signing menggunakan server backend
    qz.security.setSignaturePromise((toSign) => {
      return (resolve) => {
        // Call API backend untuk sign request
        fetch(signingServerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request: toSign }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.signature) {
              resolve(data.signature)
            } else {
              resolve(null)
            }
          })
          .catch((error) => {
            console.error('Signing server error:', error)
            resolve(null)
          })
      }
    })

    // Set public certificate
    qz.security.setCertificatePromise((resolve) => {
      resolve(certificate)
    })

    console.log('✅ QZ Tray certificate setup completed (production mode)')
  } catch (error) {
    console.error('❌ QZ Tray certificate setup failed:', error)
  }
}

/**
 * Alternatif: Menggunakan localStorage untuk menyimpan trust
 * Ini akan mengurangi frekuensi konfirmasi
 */
export const checkQZTrust = () => {
  const trusted = localStorage.getItem('qz_tray_trusted')
  return trusted === 'true'
}

export const setQZTrust = (trusted = true) => {
  localStorage.setItem('qz_tray_trusted', trusted.toString())
}

export default {
  setup: setupQZCertificate,
  setupProduction: setupQZCertificateProduction,
  checkTrust: checkQZTrust,
  setTrust: setQZTrust,
}
